import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Check, X, Crown, Zap, Star, ArrowRight, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/auth/AuthModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const [pricing, setPricing] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const { user, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchPricing();
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) return;

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        await refreshUser();
        navigate('/payment/success');
      } else if (response.data.status === 'expired') {
        // Payment expired
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }

    setCheckoutLoading(planId);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert(error.response?.data?.detail || 'Error creating checkout session');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPlanId = (tier, cycle) => {
    return `${tier}_${cycle}`;
  };

  const getPrice = (tier, cycle) => {
    const planId = getPlanId(tier, cycle);
    const plan = pricing?.plans?.find(p => p.id === planId);
    return plan?.amount || 0;
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      icon: <Star size={24} />,
      description: 'Get started with trading basics',
      features: [
        { text: '3 Beginner tutorials', included: true },
        { text: '3 trades per day', included: true },
        { text: '£10,000 virtual money', included: true },
        { text: 'ROI Calculator only', included: true },
        { text: 'Crypto market only', included: true },
        { text: 'Intermediate tutorials', included: false },
        { text: 'Advanced tutorials', included: false },
        { text: 'Gold Trading Guide', included: false },
        { text: 'All markets access', included: false },
      ],
      cta: 'Current Plan',
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: <Zap size={24} />,
      description: 'For serious learners',
      features: [
        { text: 'All Beginner + Intermediate', included: true },
        { text: 'Unlimited trades', included: true },
        { text: '£100,000 virtual money', included: true },
        { text: 'All 4 Calculators', included: true },
        { text: 'All markets access', included: true },
        { text: 'Gold Guide preview', included: true },
        { text: 'No ads', included: true },
        { text: 'Advanced tutorials', included: false },
        { text: 'Full Gold Guide', included: false },
      ],
      cta: 'Start 3-Day Trial',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium',
      icon: <Crown size={24} />,
      description: 'Complete trading mastery',
      features: [
        { text: 'All tutorials (incl. Advanced)', included: true },
        { text: 'Unlimited trades', included: true },
        { text: '£500,000 virtual money', included: true },
        { text: 'All 4 Calculators', included: true },
        { text: 'All markets access', included: true },
        { text: 'Full Gold Guide', included: true },
        { text: 'No ads', included: true },
        { text: 'Priority support', included: true },
        { text: 'Everything included', included: true },
      ],
      cta: 'Start 5-Day Trial',
      popular: false
    }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading pricing...</p>
      </div>
    );
  }

  return (
    <div className="pricing-page" data-testid="pricing-page">
      <div className="container">
        {/* Header */}
        <div className="pricing-header" data-testid="pricing-header">
          <h1>Choose Your Plan</h1>
          <p>Start free, upgrade when you're ready</p>

          {/* Billing Toggle */}
          <div className="billing-toggle" data-testid="billing-toggle">
            {['monthly', 'annual', 'lifetime'].map((cycle) => (
              <button
                key={cycle}
                className={billingCycle === cycle ? 'active' : ''}
                onClick={() => setBillingCycle(cycle)}
                data-testid={`billing-${cycle}`}
              >
                {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                {cycle === 'annual' && <span className="save-badge">Save 25%</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-grid" data-testid="pricing-grid">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              className={`pricing-card ${tier.popular ? 'popular' : ''} ${user?.subscription_tier === tier.id ? 'current' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`pricing-card-${tier.id}`}
            >
              {tier.popular && <div className="popular-badge">Most Popular</div>}
              {user?.subscription_tier === tier.id && <div className="current-badge">Current Plan</div>}

              <div className="card-header">
                <div className="tier-icon">{tier.icon}</div>
                <h3>{tier.name}</h3>
                <p>{tier.description}</p>
              </div>

              <div className="card-price">
                {tier.id === 'free' ? (
                  <>
                    <span className="price">£0</span>
                    <span className="period">forever</span>
                  </>
                ) : (
                  <>
                    <span className="price">£{getPrice(tier.id, billingCycle)}</span>
                    <span className="period">
                      {billingCycle === 'monthly' ? '/month' : billingCycle === 'annual' ? '/year' : ' one-time'}
                    </span>
                  </>
                )}
              </div>

              <ul className="features-list">
                {tier.features.map((feature, i) => (
                  <li key={i} className={feature.included ? 'included' : 'excluded'}>
                    {feature.included ? <Check size={16} /> : <X size={16} />}
                    {feature.text}
                  </li>
                ))}
              </ul>

              <button
                className={`btn ${tier.id === 'free' ? 'btn-secondary' : tier.popular ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => tier.id !== 'free' && handleSelectPlan(getPlanId(tier.id, billingCycle))}
                disabled={tier.id === 'free' || user?.subscription_tier === tier.id || checkoutLoading}
                data-testid={`select-${tier.id}-btn`}
              >
                {checkoutLoading === getPlanId(tier.id, billingCycle) ? (
                  <><Loader size={18} className="spinning" /> Processing...</>
                ) : user?.subscription_tier === tier.id ? (
                  'Current Plan'
                ) : tier.id === 'free' ? (
                  isAuthenticated ? 'Current Plan' : 'Sign Up Free'
                ) : (
                  <>{tier.cta} <ArrowRight size={18} /></>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        {/* FAQ */}
        <div className="pricing-faq" data-testid="pricing-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I cancel anytime?</h4>
              <p>Yes! You can cancel your subscription at any time. You'll retain access until the end of your billing period.</p>
            </div>
            <div className="faq-item">
              <h4>What payment methods do you accept?</h4>
              <p>We accept all major credit/debit cards through Stripe's secure payment processing.</p>
            </div>
            <div className="faq-item">
              <h4>Is the lifetime plan really forever?</h4>
              <p>Yes! Pay once and get lifetime access to all features included in your plan, including future updates.</p>
            </div>
            <div className="faq-item">
              <h4>Do free trials require payment info?</h4>
              <p>Yes, but you won't be charged until the trial ends. Cancel anytime during the trial to avoid charges.</p>
            </div>
          </div>
        </div>
      </div>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />

      <style>{`
        .pricing-page {
          padding: 40px 0 80px;
        }

        .pricing-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .pricing-header h1 {
          font-size: 3rem;
          margin-bottom: 12px;
        }

        .pricing-header p {
          color: var(--text-secondary);
          font-size: 1.125rem;
          margin-bottom: 32px;
        }

        .billing-toggle {
          display: inline-flex;
          background: var(--bg-subtle);
          padding: 4px;
          border-radius: 12px;
          gap: 4px;
        }

        .billing-toggle button {
          padding: 12px 24px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .billing-toggle button.active {
          background: var(--bg-paper);
          color: var(--text-primary);
        }

        .save-badge {
          background: var(--success-bg);
          color: var(--success);
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 64px;
        }

        .pricing-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .pricing-card.popular {
          border-color: var(--primary);
          transform: scale(1.05);
        }

        .pricing-card.current {
          border-color: var(--success);
        }

        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--primary);
          color: white;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .current-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--success);
          color: var(--bg-default);
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .card-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .tier-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-subtle);
          border-radius: 12px;
          margin: 0 auto 16px;
          color: var(--primary);
        }

        .pricing-card.popular .tier-icon {
          background: rgba(59, 130, 246, 0.1);
        }

        .card-header h3 {
          font-size: 1.5rem;
          margin-bottom: 4px;
        }

        .card-header p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .card-price {
          text-align: center;
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }

        .card-price .price {
          font-family: var(--font-heading);
          font-size: 3rem;
          font-weight: 700;
        }

        .card-price .period {
          color: var(--text-muted);
          font-size: 14px;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin-bottom: 24px;
          flex: 1;
        }

        .features-list li {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          font-size: 14px;
        }

        .features-list li.included {
          color: var(--text-secondary);
        }

        .features-list li.included svg {
          color: var(--success);
        }

        .features-list li.excluded {
          color: var(--text-muted);
        }

        .features-list li.excluded svg {
          color: var(--text-muted);
        }

        .pricing-card .btn {
          width: 100%;
          padding: 14px;
          border-radius: 8px;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* FAQ */
        .pricing-faq {
          background: var(--bg-paper);
          border-radius: 16px;
          padding: 48px;
        }

        .pricing-faq h2 {
          text-align: center;
          margin-bottom: 32px;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
        }

        .faq-item h4 {
          margin-bottom: 8px;
        }

        .faq-item p {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.6;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @media (max-width: 1024px) {
          .pricing-grid {
            grid-template-columns: 1fr;
          }

          .pricing-card.popular {
            transform: none;
          }

          .faq-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .billing-toggle {
            flex-direction: column;
            width: 100%;
          }

          .billing-toggle button {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
